// Role registry — fetched from the backend's single source of truth
// (GET /api/auth/roles) and cached in this module to back synchronous helpers.
// There is no hardcoded fallback: until the registry is hydrated the helpers
// report nothing, and access gating (AuthWrapper) waits for the fetch.

import { UserRole } from '@/types/auth';
import type { Area } from '@/config/resources';

export type ResourceAccess = Record<string, { view: boolean; edit: boolean }>;

export interface RoleInfo {
  role: string;
  login_group: string;
  is_staff: boolean;
  default_scopes: string[];
  label: string;
  resource_access: ResourceAccess;
}

export interface ResourceInfo {
  key: string;
  area: string;
}

export interface PermissionLabel {
  name: string;
  desc: string;
}

export interface RoleRegistryData {
  roles: RoleInfo[];
  resources: ResourceInfo[];
  all_permissions: string[];
  permission_labels: Record<string, PermissionLabel>;
}

const EMPTY: RoleRegistryData = {
  roles: [],
  resources: [],
  all_permissions: [],
  permission_labels: {},
};

let cache: RoleRegistryData = EMPTY;
let byRole: Record<string, RoleInfo> = {};
let areaByResource: Record<string, string> = {};

export function hydrateRoleRegistry(data: RoleRegistryData): void {
  if (!data?.roles?.length || data === cache) return;
  cache = data;
  byRole = Object.fromEntries(data.roles.map((r) => [r.role, r]));
  areaByResource = Object.fromEntries(data.resources.map((r) => [r.key, r.area]));
}

export function getRoleRegistry(): RoleRegistryData {
  return cache;
}

export function isRoleRegistryLoaded(): boolean {
  return cache.roles.length > 0;
}

const norm = (role: string | UserRole | null | undefined): string | null =>
  role ? String(role) : null;

export const isStaffRole = (role: string | UserRole | null | undefined): boolean => {
  const r = norm(role);
  return !!r && !!byRole[r]?.is_staff;
};

export const isExpertRole = (role: string | UserRole | null | undefined): boolean =>
  norm(role) === UserRole.EXPERT;

const accessOf = (role: string | UserRole | null | undefined): ResourceAccess => {
  const r = norm(role);
  return (r && byRole[r]?.resource_access) || {};
};

// Access combines the role's per-resource grant with the user's scopes:
// `view_all`/`edit_all` (admins) bypass the per-role grant — EXCEPT the
// superadmin area (org/user/role administration), which stays explicit-grant
// only so that having view_all doesn't escalate an org-admin into superadmin.
type Roleish = string | UserRole | null | undefined;

const isSuperadminResource = (resourceKey: string): boolean =>
  areaByResource[resourceKey] === 'superadmin';

// Can the user see/open a resource (drives navbar item + route gating)?
export const canViewResource = (
  role: Roleish,
  resourceKey: string,
  scopes: string[] = []
): boolean =>
  (scopes.includes('view_all') && !isSuperadminResource(resourceKey)) ||
  !!accessOf(role)[resourceKey]?.view;

// Can the user modify a resource (drives create/edit/delete buttons)?
export const canEditResource = (
  role: Roleish,
  resourceKey: string,
  scopes: string[] = []
): boolean =>
  (scopes.includes('edit_all') && !isSuperadminResource(resourceKey)) ||
  !!accessOf(role)[resourceKey]?.edit;

// Resource keys in an area the user can view, in catalog order.
export const accessibleResources = (
  role: Roleish,
  area: Area,
  catalog: { key: string; area: string }[],
  scopes: string[] = []
): string[] =>
  catalog
    .filter((r) => r.area === area && canViewResource(role, r.key, scopes))
    .map((r) => r.key);

// Does the user have ≥1 viewable resource in the area (app-switcher visibility)?
export const canAccessArea = (
  role: Roleish,
  area: Area,
  catalog: { key: string; area: string }[],
  scopes: string[] = []
): boolean =>
  catalog.some((r) => r.area === area && canViewResource(role, r.key, scopes));

// Roles an admin can assign in user-management UIs: staff roles that are not
// the committee-only expert role. When `enabledRoles` is given (org-scoped
// context), the result is additionally intersected with that org's
// enabled_roles; omit it for superadmin-wide contexts with no single org in
// play. `currentRole` (a specific user's already-assigned role) is always kept
// in the result even if the org's enabled_roles was narrowed after that
// assignment was made -- a role edit must never silently strand the user's own
// current role out of the dropdown.
export const getAssignableStaffRoles = (
  enabledRoles?: string[],
  currentRole?: string | UserRole | null
): UserRole[] => {
  const roles = cache.roles
    .filter((r) => r.is_staff && r.role !== UserRole.EXPERT && r.role !== 'MENTOR')
    .filter((r) => enabledRoles === undefined || enabledRoles.includes(r.role))
    .map((r) => r.role as UserRole);
  if (currentRole && !roles.includes(currentRole as UserRole)) {
    roles.push(currentRole as UserRole);
  }
  return roles;
};

export const getRoleScopes = (role: string | UserRole | null | undefined): string[] => {
  const r = norm(role);
  return (r && byRole[r]?.default_scopes) || [];
};

export const getRoleLabel = (role: string | UserRole | null | undefined): string => {
  const r = norm(role);
  return (r && byRole[r]?.label) || (r ? r.toLowerCase() : '');
};

export const getAllPermissions = (): string[] => cache.all_permissions;

export const getPermissionLabels = (): Record<string, PermissionLabel> =>
  cache.permission_labels;
